/**
 * WitnessCard — display a structured witness record.
 *
 * Single responsibility: render a witness's identifying metadata, predicate
 * outcomes, and admissibility decision. Generic over arbitrary "kind" labels;
 * the consumer supplies tone for the decision.
 *
 * Composition: builds on the shared `Card` surface and `KeyValueGrid`
 * primitive so witness records inherit consistent spacing, dividers, and
 * typography with the rest of the demo UI library.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo } from "react";
import { Card } from "./Card.js";
import { KeyValueGrid, type KeyValueEntry } from "./KeyValueGrid.js";
import { StatusBadge, type StatusTone } from "./StatusBadge.js";
import { FONT, GRAY } from "../design-system/tokens.js";

export interface WitnessCardField {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}

export interface WitnessCardPredicate {
  readonly id: string;
  readonly description: string;
  readonly outcome: "pass" | "fail";
}

export interface WitnessCardProps {
  readonly title: string;
  readonly decisionLabel: string;
  readonly decisionTone: StatusTone;
  readonly fields: readonly WitnessCardField[];
  readonly predicates?: readonly WitnessCardPredicate[];
}

/** Translate the public field shape into the KeyValueGrid entry shape. */
function toGridEntries(fields: readonly WitnessCardField[]): readonly KeyValueEntry[] {
  return fields.map((field) => ({
    key: field.label,
    label: field.label,
    value: field.value,
    ...(field.mono === true ? { mono: true } : {}),
  }));
}

/**
 * Renders a witness record as a card with header (title + decision badge),
 * a key/value grid of fields, and an optional predicate list.
 */
export const WitnessCard = React.memo(function WitnessCard({
  title,
  decisionLabel,
  decisionTone,
  fields,
  predicates,
}: WitnessCardProps): React.JSX.Element {
  const entries = useMemo(() => toGridEntries(fields), [fields]);
  const showPredicates = predicates !== undefined && predicates.length > 0;

  const header = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "0.5rem",
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: "0.9375rem",
          fontWeight: 600,
          color: GRAY[900],
          fontFamily: FONT.heading,
        }}
      >
        {title}
      </h3>
      <StatusBadge label={decisionLabel} tone={decisionTone} />
    </div>
  );

  return (
    <Card header={header} aria-label={title}>
      <KeyValueGrid entries={entries} dense />

      {showPredicates && (
        <div style={{ marginTop: "0.75rem" }}>
          <div
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: GRAY[500],
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "0.25rem",
            }}
          >
            Predicates
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {predicates.map((p) => (
              <li
                key={p.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.25rem 0",
                  borderBottom: `1px dashed ${GRAY[300]}`,
                  fontSize: "0.8125rem",
                }}
              >
                <StatusBadge
                  label={p.outcome === "pass" ? "PASS" : "FAIL"}
                  tone={p.outcome === "pass" ? "success" : "warn"}
                />
                <span style={{ color: GRAY[700] }}>
                  <strong style={{ fontFamily: FONT.mono, color: GRAY[900] }}>{p.id}</strong> ·{" "}
                  {p.description}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
});
